import * as React from "react";

import { getAuth } from "firebase/auth";

import config from "../../config";
import { addWebbySession } from "../../util/addWebbySession";
import fetchApi from "../../util/fetchApi";
import { navigateTo } from "../../util/navigate";

/**
 * Open the Club-in-Club member portal, optionally on a particular screen.
 *
 * The portal has no router: it seeds its navigation stack from `?screen=`, validated
 * against its own allowlist, so an unrecognised name simply opens its home. That is what
 * lets us send a member straight to booking.
 *
 * Extracted so the reservation dialogs do not each reimplement the token handoff, which
 * has three easy things to get wrong: the token must be minted fresh per open (it is
 * single-use and short-lived, hence `no-store`), the signed-in user must be re-checked
 * after the await (a member who switches account mid-flight must not be handed the
 * previous one's session), and the request needs a ceiling of its own so a hung mint does
 * not leave a button spinning forever.
 */

export interface ClubInClubTarget {
    /** A screen name from the portal's allowlist, e.g. "book". */
    screen: string;
    params?: Record<string, string>;
}

export interface ClubInClubHandoff {
    /** False when this build has no portal configured; hide the affordance. */
    available: boolean;
    opening: boolean;
    /** Set when an open failed, already phrased for a member to read. */
    error: string | null;
    open: (target?: ClubInClubTarget) => Promise<void>;
}

const TIMEOUT_MS = 10_000;

export default function useClubInClubHandoff(): ClubInClubHandoff {
    const [opening, setOpening] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const request = React.useRef<AbortController | null>(null);
    const active = React.useRef(false);

    React.useEffect(() => {
        active.current = true;
        return () => {
            active.current = false;
            request.current?.abort();
        };
    }, []);

    const open = React.useCallback(async (target?: ClubInClubTarget) => {
        // Click-only: rendering a dialog must never mint a credential.
        if (!config.clubInClubUrl || request.current) return;

        const signedInAs = getAuth().currentUser;
        if (!signedInAs) {
            setError(
                "Please sign in to Volley before opening your membership.",
            );
            return;
        }

        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
        request.current = controller;
        setOpening(true);
        setError(null);
        try {
            const { token } = await fetchApi<{ token: string }>(
                "/api/users/token",
                "GET",
                undefined,
                { signal: controller.signal, cache: "no-store" },
            );
            if (!active.current || controller.signal.aborted) return;
            if (getAuth().currentUser !== signedInAs) {
                setError("Your sign-in changed. Please try again.");
                return;
            }
            if (typeof token !== "string" || !token.trim()) {
                throw new Error("Missing handoff token");
            }

            const destination = new URL(config.clubInClubUrl);
            destination.searchParams.set("token", token);
            addWebbySession(destination);
            if (target) {
                destination.searchParams.set("screen", target.screen);
                for (const [key, value] of Object.entries(
                    target.params ?? {},
                )) {
                    destination.searchParams.set(key, value);
                }
            }
            // In place, not a new tab: the portal shows a "← Back to Volley" link in this
            // webview and replaces the cleaned URL, so both it and webview-back return
            // here. A new tab becomes a Safari sheet with its own session and no way back.
            navigateTo(destination.href);
        } catch {
            if (active.current) {
                setError("Unable to open Volley membership. Please try again.");
            }
        } finally {
            window.clearTimeout(timeout);
            request.current = null;
            if (active.current) setOpening(false);
        }
    }, []);

    return {
        available: Boolean(config.clubInClubUrl),
        opening,
        error,
        open,
    };
}
